import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { syncFromAnotherSource, getCollabState } from '@atlaskit/prosemirror-collab';
import { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ACTION } from '@atlaskit/editor-common/analytics';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import type { StepsDatabase } from '../stepsDatabase';
import type { EditorOfflineStoragePlugin } from '../editorOfflineStoragePluginType';
import { resetProsemirrorHistoryPlugin } from '../history';
import { fireOfflineStorageAnalyticsEvent } from '../analytics';

export type Status =
	| 'granted_initial'
	| 'granted_recovery'
	| 'waiting_initial'
	| 'waiting_recovery'
	| 'init';

export const nextStatus = (status: Status, granted?: boolean) => {
	switch (status) {
		case 'init':
			return granted ? 'granted_initial' : 'waiting_initial';
		case 'waiting_initial':
		case 'waiting_recovery':
			return granted ? 'granted_recovery' : 'waiting_recovery';
		case 'granted_initial':
		case 'granted_recovery':
		default:
			return status;
	}
};

export const hasHistory = (api: PublicPluginAPI<EditorOfflineStoragePlugin> | undefined) => {
	const historyState = api?.history?.sharedState?.currentState?.();
	return (historyState?.done?.eventCount ?? 0) > 0 || (historyState?.undone?.eventCount ?? 0) > 0;
};

export async function resyncOnline(
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined,
	stepsDatabase: StepsDatabase,
	editorView: EditorView,
	isActiveTab: boolean,
) {
	if (api?.connectivity?.sharedState.currentState()?.mode === 'offline') {
		return;
	}

	let status: Status = 'init';

	const lockCallback = async (granted?: boolean) => {
		status = nextStatus(status, granted);
		const { steps, docJSON, version } = await stepsDatabase.getData(editorView?.state?.schema);

		const sendResyncAnalytics = () => {
			fireOfflineStorageAnalyticsEvent({
				api,
				action: ACTION.OFFLINE_STORAGE_RESYNC_ONLINE,
				resyncStatus: status,
				isActiveTab,
			});
		};

		switch (status) {
			case 'granted_initial':
				const unconfirmedSteps = getCollabState(editorView.state)?.unconfirmed ?? [];
				if (steps.length > 0) {
					// Clear offline lock on unconfirmed steps
					unconfirmedSteps.forEach((s) => {
						if (s.origin instanceof Transaction) {
							s.origin.setMeta('isOffline', false);
						}
					});
				} else if (docJSON && version !== null && unconfirmedSteps.length !== 0) {
					// So we're here because another tab has already synced but the steps in the DB have been deleted
					// In this case we revert to the document before going offline and allow collab to resync us
					sendResyncAnalytics();
					if (hasHistory(api)) {
						resetProsemirrorHistoryPlugin(editorView);
					}
					editorView.dispatch(syncFromAnotherSource(editorView.state, version, docJSON, []));
				}
				break;

			case 'waiting_initial':
				// Reset to base document so we don't duplicate the unconfirmed steps, when we come back online we'll sync with the latest changes
				if (steps.length > 0 && docJSON && version !== null) {
					sendResyncAnalytics();

					if (hasHistory(api)) {
						resetProsemirrorHistoryPlugin(editorView);
					}
					editorView.dispatch(syncFromAnotherSource(editorView.state, version, docJSON, []));
					stepsDatabase.acquireSyncLock(lockCallback, true);
				}
				break;

			case 'granted_recovery':
				if (steps.length > 0 && docJSON && version !== null) {
					sendResyncAnalytics();

					if (hasHistory(api)) {
						resetProsemirrorHistoryPlugin(editorView);
					}
					editorView.dispatch(syncFromAnotherSource(editorView.state, version, docJSON, steps));
				}
				break;

			case 'waiting_recovery':
				if (steps.length > 0 && docJSON && version !== null) {
					sendResyncAnalytics();

					stepsDatabase.acquireSyncLock(lockCallback, true);
				}
				break;

			default:
				break;
		}
	};

	stepsDatabase.acquireSyncLock(lockCallback, false);
}
